import { lazy } from 'react'

import { GetLocationDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'

const RootPage = lazy(() => import('modules/locations/pages/settings/RootPage'))

const AddEmployeeModalPage = lazy(
  () => import('modules/locations/pages/settings/AddEmployeeModalPage')
)
const AddHolidayModalPage = lazy(
  () => import('modules/locations/pages/settings/AddHolidayModalPage')
)
const AddWorkAreaModalPage = lazy(
  () => import('modules/locations/pages/settings/AddWorkAreaModalPage')
)
const AddWorkSpaceSidebarPage = lazy(
  () => import('modules/locations/pages/settings/AddWorkSpaceSidebarPage')
)
const AuthorizedEmployeesPage = lazy(
  () => import('modules/locations/pages/settings/AuthorizedEmployeesPage')
)
const AuthorizedHolidaysPage = lazy(
  () => import('modules/locations/pages/settings/AuthorizedHolidaysPage')
)
const EditHolidayModalPage = lazy(
  () => import('modules/locations/pages/settings/EditHolidayModalPage')
)
const EditWorkAreaModalPage = lazy(
  () => import('modules/locations/pages/settings/EditWorkAreaModalPage')
)
const ImportHolidaysModalPage = lazy(
  () => import('modules/locations/pages/settings/ImportHolidaysModalPage')
)
const LocationsPages = lazy(() => import('modules/locations/pages/Locations'))
const MoveEmployeeModalPage = lazy(
  () => import('modules/locations/pages/settings/MoveEmployeeModalPage')
)
const NewLocationPage = lazy(() => import('modules/locations/pages/settings/NewLocationPage'))

const ArchivedWorkAreasPage = lazy(() => import('modules/locations/components/ArchivedWorkAreas'))

const ProtectedWorkAreasPage = lazy(
  () => import('modules/locations/pages/settings/ProtectedWorkAreasPage')
)
const RedirectToEmployeesPage = lazy(
  () => import('modules/locations/pages/settings/RedirectToEmployeesPage')
)
const RemoveLocationModalPage = lazy(
  () => import('modules/locations/pages/settings/RemoveLocationModalPage')
)

const AuthorizedWorkplaceDetailsPage = lazy(
  () => import('modules/locations/pages/settings/AuthorizedWorkplaceDetailsPage')
)

const LocationsContainer = lazy(() => import('modules/locations/components/LocationsContainer'))

const WorkSpacesPage = lazy(() => import('modules/locations/pages/settings/WorkSpacesPage'))

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.locations.path,
      element: <RootPage />,
      handle: {
        meta: () => getSettingPageMeta(navigation, 'workplaces'),
      },
      children: [
        {
          index: true,
          element: <LocationsContainer />,
        },
        {
          path: navigation.settings.locations.removeId.path,
          element: <RemoveLocationModalPage />,
        },
        {
          path: navigation.settings.locations.locationNew.path,
          element: <NewLocationPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.locations.add.title'),
            }),
          },
        },
        {
          path: navigation.settings.locations.locationsId.path,
          element: <LocationsPages />,
          handle: {
            meta: ({ params, watchQuery }) =>
              watchQuery({
                query: GetLocationDocument,
                variables: {
                  id: String(params.id),
                },
              }).map(({ data }) => {
                const locations = data?.locations.locations
                const title =
                  locations && locations.length > 0 && locations[0] ? locations[0].name : ''

                return {
                  title,
                }
              }),
          },
          children: [
            {
              index: true,
              element: <RedirectToEmployeesPage />,
            },
            {
              path: navigation.settings.locations.locationsId.workAreasArchived.path,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.work_areas.archived.title'),
                  illustration: 'input-field',
                  description: i18n.t(
                    'settings.locations.location.work_areas.archived.description'
                  ),
                }),
              },
              element: <ArchivedWorkAreasPage />,
            },
            {
              path: `${navigation.settings.locations.locationsId.workAreas.path}/:workAreaId/work-spaces`, // TODO: refactor this when we move to Space Control
              element: <WorkSpacesPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.work_areas.manage_spaces'),
                }),
              },
              children: [
                {
                  path: 'add',
                  element: <AddWorkSpaceSidebarPage />,
                },
                {
                  path: ':workSpaceId/edit',
                  element: <AddWorkSpaceSidebarPage />,
                },
              ],
            },
            {
              path: navigation.settings.locations.locationsId.details.path,
              element: <AuthorizedWorkplaceDetailsPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.tabs.details'),
                }),
              },
            },
            {
              path: navigation.settings.locations.locationsId.employees.path,
              element: <AuthorizedEmployeesPage />,
              handle: {
                meta: ({ params, watchQuery }) =>
                  watchQuery({
                    query: GetLocationDocument,
                    variables: {
                      id: String(params.id),
                    },
                  }).map(({ data }) => {
                    const locations = data?.locations.locations
                    const name =
                      locations && locations.length > 0 && locations[0] ? locations[0].name : ''

                    return {
                      title: i18n.t('settings.locations.tabs.employees'),
                      illustration: 'group',
                      description: i18n.t(
                        'settings.locations.location.employees.default_description',
                        {
                          name: name,
                        }
                      ),
                    }
                  }),
              },
              children: [
                {
                  path: navigation.settings.locations.locationsId.employees.add.path,
                  element: <AddEmployeeModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.employees.moveEmployeeId.path,
                  element: <MoveEmployeeModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.locations.locationsId.holidays.path,
              element: <AuthorizedHolidaysPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.holidays.title'),
                  description: i18n.t('settings.locations.location.holidays.description'),
                }),
              },
              children: [
                {
                  path: navigation.settings.locations.locationsId.holidays.add.path,
                  element: <AddHolidayModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.holidays.editHolidayId.path,
                  element: <EditHolidayModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.holidays.import.path,
                  element: <ImportHolidaysModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.locations.locationsId.workAreas.path,
              element: <ProtectedWorkAreasPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.work_areas.title'),
                  illustration: 'input-field',
                  description: i18n.t('settings.locations.location.work_areas.description'),
                }),
              },
              children: [
                {
                  path: navigation.settings.locations.locationsId.workAreas.add.path,
                  element: <AddWorkAreaModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.workAreas.workAreaIdEdit.path,
                  element: <EditWorkAreaModalPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
